/**
 * © 2025 Indikin.online. All rights reserved.
 * Made with ❤️ by YalaCoder.
 *
 * --- REWRITE SUMMARY (V81 - DEFINITIVE & FINAL LAYOUT FIX) ---
 * - MISSION: To permanently fix the layout bug by implementing the
 * architecturally correct pattern for parent-child DOM measurement.
 *
 * - THE DEFINITIVE FIX (Forwarding the Ref):
 * 1.  FORWARD REF: The entire component is now wrapped in `React.forwardRef`.
 * This allows the parent component (`App.jsx`) to pass a `ref` down and
 * attach it directly to this component's root `div`.
 * 2.  LOGIC REMOVAL: All internal measurement logic (`ResizeObserver`,
 * `useEffect`, and the `onHeightChange` prop) has been completely removed.
 *
 * - RESULT: This change transforms the `CookieBanner` into a simple, pure
 * presentational component. It is no longer responsible for measuring
 * itself. The parent `App.jsx` now has direct, reliable access to its DOM
 * node, allowing it to perform accurate measurements at the correct time in
 * the React lifecycle, which is the key to permanently fixing the bug.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useNavContext } from '@/context/NavigationProvider'; // --- NEW
import { FaCookieBite } from 'react-icons/fa';
import { isTWA } from '../../utils/tvDetect';

// --- REF NOT NEEDED ANYMORE (Context handles it), but keeping forwardRef signature to avoid breaking parent if it passes refs (though we'll clean that up in App.jsx too)
const CookieBanner = React.forwardRef((props, ref) => {
  const { t } = useTranslation();
  const { setConsent } = useAnalytics();
  const { setCookieBannerHeight } = useNavContext(); // --- NEW
  const localRef = React.useRef(null);

  // Combine refs if needed, or just use localRef for measurement
  React.useImperativeHandle(ref, () => localRef.current);

  React.useEffect(() => {
    if (!localRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === localRef.current) {
          const height = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
          setCookieBannerHeight(height);
        }
      }
    });

    resizeObserver.observe(localRef.current);

    return () => {
      resizeObserver.disconnect();
      setCookieBannerHeight(0); // <--- CRITICAL FIX: Reset height to 0 when unmounted
    };
  }, [setCookieBannerHeight]);


  const handleConsent = (consentChoice) => {
    setConsent(consentChoice);
  };

  if (isTWA()) return null;

  return (
    // --- FIX: Attach the forwarded ref to the root element ---
    <div ref={localRef} id="cookie-consent-banner" className="cookie-banner show" role="region" aria-label="Cookie consent banner">

      <div className="cookie-banner-content">
        <div className="cookie-text flex items-center">
          <FaCookieBite className="text-primary text-xl mr-3 flex-shrink-0" aria-hidden="true" />
          <p>
            {t('cookieBanner.text_part1')}
            <Link to="/cookie-policy" className="font-semibold text-primary hover:underline">
              {t('cookieBanner.text_policyLink')}
            </Link>
            {t('cookieBanner.text_part2')}
          </p>
        </div>
        <div className="cookie-buttons flex items-center gap-3 flex-shrink-0 mt-3 md:mt-0">
          <button id="cookie-accept-btn" onClick={() => handleConsent('accepted')} className="btn btn-primary !py-1 !px-4 !text-sm">
            {t('cookieBanner.accept')}
          </button>
          <button id="cookie-decline-btn" onClick={() => handleConsent('declined')} className="btn btn-outline !border-gray-600 !text-gray-300 hover:!bg-gray-700/50 hover:!border-gray-500 !py-1 !px-4 !text-sm">
            {t('cookieBanner.decline')}
          </button>
        </div>
      </div>
    </div>
  );
});

// Set a display name for the component for better debugging
CookieBanner.displayName = 'CookieBanner';

export default CookieBanner;
